import { PlatformError, PlatformEvent, SSE_EVENT_DATA, type SseEventType } from '@agentic/contracts';
import { errorFromResponse, resolveHeaders, type TransportOptions } from './http.ts';
import type { EventStreamHandle, EventStreamHandlers, TaskStreamEvent } from './types.ts';

/** One dispatched server-sent message. */
export interface SseMessage {
  event: string;
  data: string;
  id?: string;
  retry?: number;
}

/**
 * Incremental parser for `text/event-stream` (WHATWG rules: `\n`, `\r\n` or `\r` line ends, `:` comments,
 * multi-line `data`, dispatch on a blank line). Feed decoded text chunks; split points may fall anywhere.
 */
export function createSseParser(onMessage: (message: SseMessage) => void): {
  feed(chunk: string): void;
} {
  let buffer = '';
  let event = '';
  let data: string[] = [];
  let id: string | undefined;
  let retry: number | undefined;

  const dispatch = (): void => {
    if (data.length > 0) {
      onMessage({
        event: event || 'message',
        data: data.join('\n'),
        ...(id === undefined ? {} : { id }),
        ...(retry === undefined ? {} : { retry }),
      });
    }
    event = '';
    data = [];
    retry = undefined;
  };

  const line = (text: string): void => {
    if (text === '') {
      dispatch();
      return;
    }
    if (text.startsWith(':')) return;
    const colon = text.indexOf(':');
    const field = colon === -1 ? text : text.slice(0, colon);
    let value = colon === -1 ? '' : text.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
    else if (field === 'id' && !value.includes('\0')) id = value;
    else if (field === 'retry' && /^\d+$/.test(value)) retry = Number(value);
  };

  return {
    feed(chunk: string) {
      buffer += chunk;
      for (;;) {
        const match = /\r\n|\r|\n/.exec(buffer);
        if (!match) break;
        // A trailing `\r` may be the first half of `\r\n`; wait for more input.
        if (match[0] === '\r' && match.index === buffer.length - 1) break;
        line(buffer.slice(0, match.index));
        buffer = buffer.slice(match.index + match[0].length);
      }
    },
  };
}

function isKnownType(type: string): type is SseEventType {
  return Object.hasOwn(SSE_EVENT_DATA, type);
}

/** Validates a raw SSE message into a typed task event, or returns the envelope for unknown/invalid data. */
export function decodeStreamEvent(
  message: SseMessage,
): { kind: 'known'; event: TaskStreamEvent } | { kind: 'other'; event: PlatformEvent } | { kind: 'invalid' } {
  let json: unknown;
  try {
    json = JSON.parse(message.data);
  } catch {
    return { kind: 'invalid' };
  }
  const envelope = PlatformEvent.safeParse(json);
  if (!envelope.success) return { kind: 'invalid' };
  const type = envelope.data.type;
  if (isKnownType(type)) {
    const data = SSE_EVENT_DATA[type].safeParse(envelope.data.data);
    if (data.success) {
      return { kind: 'known', event: { ...envelope.data, type, data: data.data } as TaskStreamEvent };
    }
  }
  return { kind: 'other', event: envelope.data };
}

export interface OpenEventStreamOptions extends TransportOptions {
  /** Initial reconnect delay; doubles up to `maxRetryMs`. */
  retryMs?: number;
  maxRetryMs?: number;
}

/**
 * Opens a resilient SSE subscription with `fetch` (so cookies and headers work in browsers and Bun).
 * Reconnects with backoff and `Last-Event-ID`, de-duplicates by event id, stops on 4xx.
 */
export function openEventStream(
  url: string,
  handlers: EventStreamHandlers,
  options: OpenEventStreamOptions,
): EventStreamHandle {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const controller = new AbortController();
  const seen = new Set<string>();
  const seenOrder: string[] = [];
  let lastEventId: string | undefined;
  let closed = false;
  let delay = options.retryMs ?? 1000;
  const maxDelay = options.maxRetryMs ?? 15_000;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const remember = (id: string): boolean => {
    if (seen.has(id)) return false;
    seen.add(id);
    seenOrder.push(id);
    if (seenOrder.length > 1000) {
      const drop = seenOrder.shift();
      if (drop !== undefined) seen.delete(drop);
    }
    return true;
  };

  const onMessage = (message: SseMessage): void => {
    if (message.id !== undefined) lastEventId = message.id;
    if (message.retry !== undefined) delay = message.retry;
    const decoded = decodeStreamEvent(message);
    if (decoded.kind === 'invalid') return;
    if (!remember(decoded.event.id)) return;
    if (decoded.kind === 'known') handlers.onEvent(decoded.event);
    else handlers.onOtherEvent?.(decoded.event);
  };

  const scheduleReconnect = (): void => {
    if (closed) return;
    handlers.onStatus?.('reconnecting');
    timer = setTimeout(connect, delay);
    delay = Math.min(delay * 2, maxDelay);
  };

  async function connect(): Promise<void> {
    if (closed) return;
    handlers.onStatus?.('connecting');
    try {
      const res = await doFetch(url, {
        method: 'GET',
        headers: {
          accept: 'text/event-stream',
          ...resolveHeaders(options),
          ...(lastEventId === undefined ? {} : { 'last-event-id': lastEventId }),
        },
        credentials: options.credentials ?? 'include',
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        const error = res.ok
          ? new PlatformError('internal', 'Event stream has no body')
          : await errorFromResponse(res, 'tasks.events');
        handlers.onError?.(error);
        if (res.status >= 400 && res.status < 500) {
          closed = true;
          handlers.onStatus?.('closed');
          return;
        }
        scheduleReconnect();
        return;
      }
      handlers.onStatus?.('open');
      delay = options.retryMs ?? 1000;
      const parser = createSseParser(onMessage);
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.feed(value);
      }
      scheduleReconnect();
    } catch (cause) {
      if (closed) return;
      handlers.onError?.(
        new PlatformError('downstream_failed', 'Event stream interrupted', { cause, retryable: true }),
      );
      scheduleReconnect();
    }
  }

  void connect();

  return {
    close() {
      if (closed) return;
      closed = true;
      if (timer) clearTimeout(timer);
      controller.abort();
      handlers.onStatus?.('closed');
    },
    get closed() {
      return closed;
    },
  };
}
